import { InlineProtocolV3Connection, InlineProtocolV3Error, } from "./v3-connection.js";
const cloneAuthorization = (value) => ({
    ...value,
    key: value.key.slice(),
    keyId: value.keyId.slice(),
});
const cloneCredentials = (value) => ({
    permanent: cloneAuthorization(value.permanent),
    ...(value.temporary ? { temporary: cloneAuthorization(value.temporary) } : {}),
});
export class InlineRealtimeV3Client {
    #options;
    #login;
    #session;
    #credentials;
    #generation = 0;
    #activeSessionCalls = 0;
    #rotationDueSession;
    #rotationTask;
    #rotationError;
    constructor(options) {
        this.#options = options;
    }
    get credentials() {
        if (!this.#credentials)
            return undefined;
        const credentials = cloneCredentials(this.#credentials);
        if (this.#session)
            credentials.temporary = this.#session.authorization;
        if (this.#login)
            credentials.permanent = this.#login.authorization;
        return credentials;
    }
    get authenticated() { return this.#session !== undefined; }
    async beginLogin(request) {
        await this.close();
        this.#login = await InlineProtocolV3Connection.connect(this.#connectionOptions());
        this.#credentials = { permanent: this.#login.authorization };
        return await this.#login.authBegin(request);
    }
    async completeLogin(request) {
        const login = this.#login;
        if (!login || !this.#credentials)
            throw new InlineProtocolV3Error("unauthorized", "Native login has not started");
        const result = await login.authComplete(request);
        this.#credentials.permanent = login.authorization;
        if (result.state.oneofKind === "authorized")
            await this.#replaceAuthenticatedSession();
        return result;
    }
    async connect(credentials) {
        await this.close();
        const generation = this.#generation;
        this.#credentials = cloneCredentials(credentials);
        const temporary = this.#credentials.temporary;
        if (temporary && temporary.expiresAt !== undefined) {
            const opened = await this.#openTemporarySession(generation, {
                authorization: temporary,
            });
            const session = opened.session;
            try {
                await session.probeTemporaryAuthorization();
                if (!session.temporaryAuthorizationNeedsRotation()) {
                    this.#requireCurrent(generation);
                    this.#session = session;
                    if (opened.rotationDue)
                        this.#handleRotationDue(session, generation);
                    return;
                }
            }
            catch (error) {
                if (!(error instanceof InlineProtocolV3Error) || error.code !== "unauthorized") {
                    await session.close();
                    throw error;
                }
            }
            await session.close();
        }
        await this.#replaceAuthenticatedSession(generation);
    }
    async reconnect() {
        const credentials = this.credentials;
        if (!credentials)
            throw new InlineProtocolV3Error("unauthorized", "No Inline Protocol credentials are available");
        await this.connect(credentials);
    }
    async callRpc(rpc) {
        return await this.#withSessionCall((session) => session.callRpc(rpc));
    }
    async createHttpUpload(request) {
        return await this.#withSessionCall((session) => session.createHttpUpload(request));
    }
    async finishHttpUpload(request) {
        return await this.#withSessionCall((session) => session.finishHttpUpload(request));
    }
    async close() {
        ++this.#generation;
        const connections = [this.#login, this.#session].filter((value) => value !== undefined);
        const rotationTask = this.#rotationTask;
        this.#login = undefined;
        this.#session = undefined;
        this.#rotationDueSession = undefined;
        this.#rotationError = undefined;
        await Promise.allSettled([
            ...connections.map((connection) => connection.close()),
            ...(rotationTask ? [rotationTask] : []),
        ]);
    }
    async #replaceAuthenticatedSession(generation = this.#generation, expectedSession) {
        const credentials = this.#credentials;
        if (!credentials)
            throw new InlineProtocolV3Error("unauthorized", "Permanent authorization key is unavailable");
        if (expectedSession && this.#session !== expectedSession)
            return;
        const previousSession = this.#session;
        this.#session = undefined;
        await previousSession?.close();
        this.#requireCurrent(generation);
        const opened = await this.#openTemporarySession(generation, {
            temporary: true,
        });
        const session = opened.session;
        try {
            await session.bindTemporary(credentials.permanent);
            this.#requireCurrent(generation);
            const nextCredentials = cloneCredentials(credentials);
            nextCredentials.temporary = session.authorization;
            await this.#options.onCredentials?.(cloneCredentials(nextCredentials));
            this.#requireCurrent(generation);
            this.#credentials = nextCredentials;
        }
        catch (error) {
            await session.close();
            throw error;
        }
        this.#session = session;
        this.#rotationError = undefined;
        if (this.#rotationDueSession === expectedSession)
            this.#rotationDueSession = undefined;
        if (opened.rotationDue)
            this.#handleRotationDue(session, generation);
        const login = this.#login;
        this.#login = undefined;
        await login?.close();
    }
    async #openTemporarySession(generation, authorization) {
        const owner = { rotationDue: false };
        const session = await InlineProtocolV3Connection.connect({
            ...this.#connectionOptions(),
            ...authorization,
            onRotationDue: () => {
                owner.rotationDue = true;
                if (owner.session)
                    this.#handleRotationDue(owner.session, generation);
            },
        });
        owner.session = session;
        return { session, rotationDue: owner.rotationDue };
    }
    #handleRotationDue(session, generation) {
        if (generation !== this.#generation || this.#session !== session)
            return;
        this.#rotationDueSession = session;
        this.#maybeRotate();
    }
    #maybeRotate() {
        const session = this.#rotationDueSession;
        if (!session || this.#session !== session || this.#activeSessionCalls > 0 || this.#rotationTask)
            return;
        const generation = this.#generation;
        const task = this.#replaceAuthenticatedSession(generation, session);
        this.#rotationTask = task;
        void task
            .catch((error) => {
            if (generation !== this.#generation)
                return;
            this.#rotationError = error instanceof Error ? error : new Error(String(error));
        })
            .finally(() => {
            if (this.#rotationTask === task)
                this.#rotationTask = undefined;
            if (generation === this.#generation)
                this.#maybeRotate();
        });
    }
    async #withSessionCall(operation) {
        if (this.#rotationTask)
            await this.#rotationTask;
        if (this.#rotationDueSession) {
            this.#maybeRotate();
            if (this.#rotationTask)
                await this.#rotationTask;
        }
        if (this.#rotationError)
            throw this.#rotationError;
        const session = this.#requireSession();
        this.#activeSessionCalls += 1;
        try {
            return await operation(session);
        }
        finally {
            this.#activeSessionCalls -= 1;
            this.#maybeRotate();
        }
    }
    #requireCurrent(generation) {
        if (generation !== this.#generation) {
            throw new InlineProtocolV3Error("closed", "Inline Protocol client lifecycle changed");
        }
    }
    #requireSession() {
        if (!this.#session)
            throw new InlineProtocolV3Error("unauthorized", "Inline Protocol authenticated session is not connected");
        return this.#session;
    }
    #connectionOptions() {
        return {
            url: this.#options.url,
            rsaPublicKeys: this.#options.rsaPublicKeys,
            connectTimeoutMs: this.#options.connectTimeoutMs,
            requestTimeoutMs: this.#options.requestTimeoutMs,
            onUpdate: this.#options.onUpdate,
        };
    }
}
